/**
 * ForecastEvolutionChart Component
 *
 * Displays how forecasts have evolved across different model runs
 */

import * as React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { cn } from "@/lib/utils";

// Types
export interface TrendDataPoint {
  run: string;
  runTimestamp: number;
  date: string;
  nodeId: string;
  [key: string]: string | number;
}

export interface TrendLineConfig {
  key: string;
  label: string;
  color: string;
  strokeDasharray?: string;
}

export interface ForecastEvolutionChartProps {
  data: TrendDataPoint[];
  lines: TrendLineConfig[];
  /** Pixel height, or a percentage (e.g. `"100%"`) to fill a flex parent */
  height?: number | string;
  yAxisLabel?: string;
  className?: string;
  showGrid?: boolean;
  showTooltip?: boolean;
}

// Semantic colors for forecast lines
const LINE_COLORS: Record<string, string> = {
  estimate25th: "#93c5fd", // Light blue
  estimate90th: "#93c5fd", // Light blue
  predicted_median: "#3b82f6", // Primary blue
  estimate75th: "#1d4ed8", // Dark blue
  best_estimate: "#1e3a8a", // Darkest blue
};

// Format compact numbers
function formatCompactNumber(value: number): string {
  const absValue = Math.abs(value);
  if (absValue >= 1000000) {
    return (value / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (absValue >= 1000) {
    return (value / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  } else if (absValue >= 1) {
    return value.toFixed(0);
  }
  return value.toFixed(2);
}

// Format X-axis label (run time)
function formatRunTime(timestamp: number, dataLength: number): string {
  const date = new Date(timestamp);

  if (dataLength > 10) {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = date.getHours();
    const isPM = hour >= 12;
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${month}/${day} ${displayHour}${isPM ? "PM" : "AM"}`;
  }

  return (
    date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }) +
    " " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  );
}

// Custom tooltip component
function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  const runDate = new Date(label);
  const formattedRunTime = runDate.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div className="p-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
      <p className="font-medium text-gray-900 dark:text-white mb-2">
        Model Run: {formattedRunTime}
      </p>
      {payload.map((entry: any, index: number) => (
        <div
          key={index}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
        >
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="font-medium">{entry.name}:</span>
          <span>{formatCompactNumber(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function ForecastEvolutionChart({
  data,
  lines,
  height = 300,
  yAxisLabel = "Value",
  className,
  showGrid = true,
  showTooltip = true,
}: ForecastEvolutionChartProps) {
  const fillsParent =
    typeof height === "string" && height.trim().endsWith("%");
  // Sort data by timestamp for proper display (oldest to newest for chart)
  const sortedData = React.useMemo(() => {
    return [...data].sort((a, b) => a.runTimestamp - b.runTimestamp);
  }, [data]);

  return (
    <div
      className={cn("w-full", fillsParent && "h-full min-h-0", className)}
      style={{ height }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={sortedData}
          margin={{ top: 10, right: 20, left: 20, bottom: fillsParent ? 36 : 30 }}
        >
          {/* Grid */}
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e7eb"
              className="dark:stroke-gray-700"
            />
          )}

          {/* X Axis - using runTimestamp */}
          <XAxis
            dataKey="runTimestamp"
            type="number"
            scale="time"
            domain={["dataMin", "dataMax"]}
            stroke="#6b7280"
            fontSize={12}
            tickLine={{ stroke: "#6b7280" }}
            axisLine={{ stroke: "#6b7280" }}
            tickFormatter={(value) => formatRunTime(value, sortedData.length)}
            label={{
              value: "Model Run Time",
              position: "insideBottom",
              offset: -20,
              style: { textAnchor: "middle", fill: "#6b7280" },
            }}
          />

          {/* Y Axis */}
          <YAxis
            stroke="#6b7280"
            fontSize={12}
            tickLine={{ stroke: "#6b7280" }}
            axisLine={{ stroke: "#6b7280" }}
            tickFormatter={formatCompactNumber}
            label={
              yAxisLabel
                ? {
                    value: yAxisLabel,
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle", fill: "#6b7280" },
                  }
                : undefined
            }
          />

          {/* Tooltip */}
          {showTooltip && (
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: "#9ca3af", strokeWidth: 1 }}
            />
          )}

          {/* Lines */}
          {lines.map((lineConfig) => (
            <Line
              key={lineConfig.key}
              type="monotone"
              dataKey={lineConfig.key}
              name={lineConfig.label}
              stroke={
                lineConfig.color || LINE_COLORS[lineConfig.key] || "#3b82f6"
              }
              strokeWidth={lineConfig.key === "best_estimate" ? 3 : 2}
              strokeDasharray={
                lineConfig.key === "best_estimate"
                  ? "5 5"
                  : lineConfig.strokeDasharray
              }
              connectNulls
              dot={{
                fill:
                  lineConfig.color || LINE_COLORS[lineConfig.key] || "#3b82f6",
                strokeWidth: 2,
                r: 4,
              }}
              activeDot={{ r: 6, strokeWidth: 2 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Helper to create line configs
export function createEvolutionLineConfigs(
  keys: string[],
  labels?: string[],
): TrendLineConfig[] {
  const defaultLabels: Record<string, string> = {
    estimate90th: "90th Percentile",
    predicted_median: "Median",
    estimate75th: "75th Percentile",
    best_estimate: "Best Estimate",
  };

  return keys.map((key, index) => ({
    key,
    label: labels?.[index] || defaultLabels[key] || key,
    color: LINE_COLORS[key] || "#3b82f6",
  }));
}

export default ForecastEvolutionChart;
